"use client"

import {useEffect, useState} from "react"
import Link from "next/link"
import Image from "next/image"
import {Button} from "@/components/ui/button"
import {Menu, X} from "lucide-react"
import LanguageSwitcher from "@/components/language-switcher"
import {ThemeToggle} from "@/components/theme-toggle"
import {useI18n} from "@/lib/i18n"
import {useAuth} from "@/lib/auth-context"
import {useHoverAnimation} from "@/hooks/use-hover-animation"
import ReactCountryFlag from "react-country-flag"

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isClient, setIsClient] = useState(false)
  const { t, loadTranslations } = useI18n()
  const { isLoggedIn } = useAuth()
  
  // Animation hook for logo hover (MDC Principle: MICRO-FEEDBACK)
  const logoHover = useHoverAnimation(1.05, 150)

  // Ensure client-side rendering for auth state
  useEffect(() => {
    setIsClient(true)
  }, [])

  // Load header translations
  useEffect(() => {
    loadTranslations("header")
  }, [loadTranslations])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <header
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-sm ${
        isScrolled ? "bg-white/95 dark:bg-gray-900/95 shadow-lg" : "bg-white/95 dark:bg-gray-900/95"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-20">
        <Link
          href="/"
          className={`flex items-center space-x-2.5 sm:space-x-3 group ${logoHover.hoverProps.className}`}
          aria-label="MyJobAI.de Home"
          onMouseEnter={logoHover.hoverProps.onMouseEnter}
          onMouseLeave={logoHover.hoverProps.onMouseLeave}
          style={logoHover.hoverProps.style}
        >
          <div className="relative h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 flex-shrink-0">
            <Image
              src="/logo.png"
              alt="MyJobAI.de Logo"
              width={48}
              height={48}
              className="object-contain dark:brightness-0 dark:invert transition-transform duration-200 group-hover:scale-110"
              priority
              style={{ willChange: "transform" }}
            />
            <div 
              className="absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 md:-bottom-1.5 md:-right-1.5 transition-transform duration-200 group-hover:scale-110 z-10" 
              aria-label="Germany flag"
            >
              <ReactCountryFlag
                countryCode="DE"
                svg
                style={{
                  width: '0.75rem',
                  height: '0.5625rem',
                }}
                className="w-3 h-2.25 sm:w-3.5 sm:h-2.625 md:w-4 md:h-3 rounded-sm object-cover"
              />
            </div>
          </div>
          <div className="flex flex-col justify-center leading-tight">
            <span className={`text-base sm:text-lg md:text-xl font-bold tracking-tight transition-colors duration-200 ${isScrolled ? "text-gray-900 dark:text-white" : "text-gray-900 dark:text-white"}`}>
              MyJobAI.de
            </span>
            <span className={`text-[10px] sm:text-xs leading-tight transition-colors duration-200 ${isScrolled ? "text-gray-600 dark:text-gray-400" : "text-gray-600 dark:text-gray-400"} hidden sm:block mt-0.5`}>
              {t("header.never_give_up") || "never give up!"}
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1" aria-label="Main navigation">
          <Link
            href="/#benefits"
            className="px-2 xl:px-3 py-2 rounded-lg text-xs xl:text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 relative group whitespace-nowrap"
          >
            {t("benefits")}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
          <Link
            href="/#pricing"
            className="px-2 xl:px-3 py-2 rounded-lg text-xs xl:text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 relative group whitespace-nowrap"
          >
            {t("pricing")}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
          <Link
            href="/faq"
            className="px-2 xl:px-3 py-2 rounded-lg text-xs xl:text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 relative group whitespace-nowrap"
          >
            {t("faq") || "FAQ"}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
          <Link
            href="/education-providers"
            className="px-2 xl:px-3 py-2 rounded-lg text-xs xl:text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 relative group whitespace-nowrap"
          >
            {t("for_education_providers")}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
          <Link
            href="/companies-landing"
            className="px-2 xl:px-3 py-2 rounded-lg text-xs xl:text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 relative group whitespace-nowrap"
          >
            {t("for_companies")}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
          <Link
            href="/employer"
            className="px-2 xl:px-3 py-2 rounded-lg text-xs xl:text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 relative group whitespace-nowrap"
          >
            {t("employer_portal") || "Employer Login"}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>
          <Link
            href="/career-coaches"
            className="px-2 xl:px-3 py-2 rounded-lg text-xs xl:text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 relative group whitespace-nowrap"
          >
            {t("for_coaches")}
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary group-hover:w-full transition-all duration-200"></span>
          </Link>

          <div className="h-6 w-px bg-gray-300 dark:bg-gray-700 mx-1" aria-hidden="true" />

          <LanguageSwitcher />
          <ThemeToggle />
          {!isClient ? (
            // Show loading state during hydration
            <div className="ml-1 xl:ml-2 h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          ) : isLoggedIn ? (
            <Link href="/dashboard">
              <Button className="ml-1 xl:ml-2 bg-purple-600 hover:bg-purple-700 text-white text-xs xl:text-sm px-2 xl:px-4">Log in to Dashboard</Button>
            </Link>
          ) : (
            <>
              <Link href="/auth" className="ml-2 xl:ml-4">
                <Button variant="outline" className="bg-transparent text-sm xl:text-base px-4 xl:px-5 py-2 border-gray-300 hover:border-gray-400 hover:border-primary transition-all duration-200 hover:scale-105">
                  {t("login")}
                </Button>
              </Link>
              <Link href="/auth?tab=register">
                <Button className="bg-primary text-white hover:bg-primary/90 text-sm xl:text-base px-4 xl:px-6 py-2 shadow-sm hover:shadow-lg transition-all duration-200 hover:scale-105">
                  {t("signup")}
                </Button>
              </Link>
            </>
          )}
        </nav>

        {/* Tablet Navigation (768px - 1023px) */}
        <nav className="hidden md:flex lg:hidden items-center space-x-1">
          <Link
            href="/#benefits"
            className={`px-2 py-2 rounded-md text-xs font-medium ${
              isScrolled ? "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            {t("benefits")}
          </Link>
          <Link
            href="/#pricing"
            className={`px-2 py-2 rounded-md text-xs font-medium ${
              isScrolled ? "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            {t("pricing")}
          </Link>
          <Link
            href="/faq"
            className={`px-2 py-2 rounded-md text-xs font-medium ${
              isScrolled ? "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            {t("faq") || "FAQ"}
          </Link>
          <Link
            href="/education-providers"
            className={`px-2 py-2 rounded-md text-xs font-medium ${
              isScrolled ? "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white" : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            {t("for_education_providers")}
          </Link>
          <LanguageSwitcher />
          <ThemeToggle />
          {!isClient ? (
            // Show loading state during hydration
            <div className="ml-1 h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          ) : isLoggedIn ? (
            <Link href="/dashboard">
              <Button className="ml-3 bg-primary hover:bg-primary/90 text-white text-xs px-2">Log in to Dashboard</Button>
            </Link>
          ) : (
            <>
              <Link href="/auth">
                <Button variant="outline" className="ml-3 bg-transparent text-xs px-2">
                  {t("login")}
                </Button>
              </Link>
              <Link href="/auth?tab=register">
              <Button className="ml-1 bg-primary text-white hover:bg-primary/90 text-xs px-2">{t("signup")}</Button>
              </Link>
            </>
          )}
        </nav>

        {/* Mobile menu button */}
        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle />
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-110"
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X className="h-6 w-6 transition-transform duration-200" /> : <Menu className="h-6 w-6 transition-transform duration-200" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white dark:bg-gray-900 shadow-2xl border-t border-gray-200 dark:border-gray-700 animate-in slide-in-from-top duration-200">
          <div className="px-4 pt-4 pb-5 space-y-1">
            <Link
              href="#benefits"
              className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:translate-x-2 group relative"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("benefits")}
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-primary group-hover:h-6 transition-all duration-200 rounded-r-full"></span>
            </Link>
            <Link
              href="/#pricing"
              className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:translate-x-2 group relative"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("pricing")}
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-primary group-hover:h-6 transition-all duration-200 rounded-r-full"></span>
            </Link>
            <Link
              href="/faq"
              className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:translate-x-2 group relative"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("faq") || "FAQ"}
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-primary group-hover:h-6 transition-all duration-200 rounded-r-full"></span>
            </Link>
            <Link
              href="/education-providers"
              className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:translate-x-2 group relative"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("for_education_providers")}
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-primary group-hover:h-6 transition-all duration-200 rounded-r-full"></span>
            </Link>
            <Link
              href="/companies-landing"
              className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:translate-x-2 group relative"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("for_companies")}
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-primary group-hover:h-6 transition-all duration-200 rounded-r-full"></span>
            </Link>
            <Link
              href="/employer"
              className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:translate-x-2 group relative"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("employer_portal") || "Employer Login"}
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-primary group-hover:h-6 transition-all duration-200 rounded-r-full"></span>
            </Link>
            <Link
              href="/career-coaches"
              className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:translate-x-2 group relative"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("for_coaches")}
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-primary group-hover:h-6 transition-all duration-200 rounded-r-full"></span>
            </Link>
            <Link
              href="/"
              className="block px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:translate-x-2 group relative"
              onClick={() => setIsMenuOpen(false)}
            >
              {t("home")}
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-primary group-hover:h-6 transition-all duration-200 rounded-r-full"></span>
            </Link>
            <div className="pt-4 pb-3 border-t border-gray-200 dark:border-gray-700">
              {!isClient ? (
                // Show loading state during hydration
                <div className="flex items-center px-3">
                  <div className="w-full h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                </div>
              ) : isLoggedIn ? (
                <div className="flex items-center px-3">
                  <Link href="/dashboard" className="block w-full">
                    <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm" onClick={() => setIsMenuOpen(false)}>
                      Log in to Dashboard
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  <Link href="/auth" className="block w-full">
                    <Button variant="outline" className="w-full mb-3 bg-transparent text-base py-3 border-gray-300 hover:border-primary transition-all duration-200 hover:scale-105">
                      {t("login")}
                    </Button>
                  </Link>
                  <Link href="/auth?tab=register" className="block w-full">
                    <Button className="w-full bg-primary hover:bg-primary/90 text-white text-base py-3 shadow-sm hover:shadow-lg transition-all duration-200 hover:scale-105">
                      {t("signup")}
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
